/**
 * DesignPreviewPage — Anteprima direzione grafica Gestionale di Studio v2
 *
 * Uso: aprire l'app con `#design-preview` in URL (es. https://.../#design-preview).
 * Pagina statica di mockup, non collegata al DB, non modifica dati.
 * Serve a validare la direzione grafica prima del rollout su tutte le pagine.
 *
 * Rispetta il Brand Book Gruppo Impresa Suite v1.0 (Maggio 2025):
 * - Colore Studio: #0B6B4F
 * - Font UI: Inter
 * - Iconografia: lucide-react (lineare, 20-24px)
 *
 * 12 varianti richieste dall'utente:
 *   1. Login del Gestionale di Studio
 *   2. Cruscotto desktop con menu aperto
 *   3. Cruscotto desktop con menu compatto
 *   4. Cruscotto notebook (1280px)
 *   5. Cruscotto tablet (900px)
 *   6. Sezione Fatturazione con sottomenu
 *   7. Sezione Clienti con sottomenu
 *   8. Fascia aziendale completa
 *   9. Fascia aziendale compatta
 *  10. Esempio di tabella
 *  11. Esempio di modulo di inserimento
 *  12. Esempio di finestra di conferma critica
 */
import React, { useState } from "react";
import {
  Home,
  FileText,
  BookUser,
  Receipt,
  Users,
  CalendarClock,
  BarChart3,
  FileSignature,
  Settings,
  UserSquare2,
  ChevronLeft,
  ChevronRight,
  Bell,
  ClipboardList,
  LogOut,
  Building2,
  Phone,
  Mail,
  ShieldCheck,
  Landmark,
  Copy,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  ChevronDown,
  AlertTriangle,
  X,
  Check,
  Briefcase,
  UserPlus,
  FolderPlus,
  ReceiptText,
  StickyNote,
  Calendar,
  ArrowUpRight,
  Printer,
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════
// TOKEN DESIGN SYSTEM (Brand Book v1.0)
// ═══════════════════════════════════════════════════════════════
const T = {
  greenPrimary: "#0B6B4F",
  greenTint: "rgba(11,107,79,0.08)",
  greenTintHover: "rgba(11,107,79,0.14)",
  greenLight: "#2EA97A",
  orange: "#F28C28",
  red: "#D64545",
  blue: "#1F6FB2",
  inkDark: "#10233F",
  inkBody: "#1F2A24",
  inkSecondary: "#6B7280",
  inkMuted: "#9CA3AF",
  bgPage: "#F7F8F9",
  bgSurface: "#FFFFFF",
  borderSoft: "#E5E7EB",
  borderStrong: "#D1D5DB",
  font: "'Inter', system-ui, -apple-system, sans-serif",
};

// ═══════════════════════════════════════════════════════════════
// DATI MOCKUP (statici, non toccano il DB)
// ═══════════════════════════════════════════════════════════════
const STUDIO = {
  ragioneSociale: "Gruppo Impresa Srl STP",
  sede: "Civitavecchia",
  studioTelefono: "0766 581701",
  studioEmail: "info@gruppoimpresa.org",
  studioPec: "gruppoimpresa@pec.it",
  studioSdi: "M5UXCR1",
  agenziaTelefono: "0766 500123",
  agenziaEmail: "agenzia@gruppoimpresa.org",
  agenziaPec: "agenzia@pec.gruppoimpresa.it",
  rea: "RM-1594875",
  codiceFiscale: "15948751001",
  partitaIva: "15948751001",
  banca: "Intesa Sanpaolo",
  iban: "IT60 X054 2811 1010 0000 0123 456",
};

const NAV = [
  { value: "dashboard", label: "Cruscotto", icon: Home },
  { value: "cases", label: "Cruscotto Pratiche", icon: FileText },
  { value: "attivita", label: "Attività Operative", icon: BookUser },
  { value: "fatturazione", label: "Fatturazione", icon: Receipt },
  { value: "clients", label: "Clienti", icon: Users },
  { value: "scadenze", label: "Scadenzario", icon: CalendarClock },
  { value: "report", label: "Report", icon: BarChart3 },
  { value: "cliente360", label: "Cliente 360°", icon: UserSquare2 },
  { value: "modulistica", label: "Modulistica", icon: FileSignature },
  { value: "impostazioni", label: "Impostazioni", icon: Settings },
];

const SUBNAV = {
  dashboard: [
    { key: "overview", label: "Panoramica" },
    { key: "urgenti", label: "Urgenti" },
    { key: "mielavoro", label: "Il mio lavoro" },
  ],
  fatturazione: [
    { key: "elenco", label: "Elenco fatture" },
    { key: "nuova", label: "Nuova fattura" },
    { key: "preventivi", label: "Preventivi" },
    { key: "incassi", label: "Incassi" },
    { key: "solleciti", label: "Solleciti" },
    { key: "config", label: "Configurazione" },
  ],
  clients: [
    { key: "elenco", label: "Elenco clienti" },
    { key: "nuovo", label: "Nuovo cliente" },
    { key: "c360", label: "Cliente 360°" },
    { key: "documenti", label: "Documenti" },
    { key: "attivita", label: "Attività" },
    { key: "comunicazioni", label: "Comunicazioni" },
  ],
  cases: [
    { key: "cruscotto", label: "Cruscotto" },
    { key: "elenco", label: "Elenco pratiche" },
    { key: "nuova", label: "Nuova pratica" },
    { key: "attivita", label: "Attività" },
    { key: "diario", label: "Diario" },
    { key: "documenti", label: "Documenti" },
    { key: "scadenze", label: "Scadenze" },
  ],
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTI DESIGN SYSTEM (mockup UI)
// ═══════════════════════════════════════════════════════════════

/** Barra superiore compatta bianca */
function TopBar({ compact = false }) {
  return (
    <div
      style={{
        height: 56,
        backgroundColor: T.bgSurface,
        borderBottom: `1px solid ${T.borderSoft}`,
        display: "flex",
        alignItems: "center",
        padding: "0 24px",
        gap: 16,
        flexShrink: 0,
      }}
    >
      {/* Brand */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            backgroundColor: T.greenPrimary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          <Briefcase size={16} />
        </div>
        <div style={{ lineHeight: 1.1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.inkDark, letterSpacing: "-0.005em" }}>
            Gruppo Impresa
          </div>
          <div style={{ fontSize: 9, fontWeight: 600, color: T.greenPrimary, letterSpacing: "0.18em", marginTop: -1 }}>
            SUITE · GESTIONALE DI STUDIO
          </div>
        </div>
      </div>

      {/* Separator */}
      <div style={{ width: 1, height: 24, backgroundColor: T.borderSoft }} />

      {/* Company */}
      <div style={{ minWidth: 0, flex: 1, lineHeight: 1.15 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.inkDark, letterSpacing: "-0.005em" }}>
          {STUDIO.ragioneSociale}
        </div>
        <div style={{ fontSize: 11, color: T.inkSecondary }}>{STUDIO.sede}</div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
        <IconBtn icon={Bell} badge="12" title="Notifiche" />
        <IconBtn icon={ClipboardList} badge="7" title="Il mio lavoro" />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 10px 4px 4px",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              backgroundColor: T.greenPrimary,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            PB
          </div>
          {!compact && (
            <>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.inkDark }}>Paola</span>
              <ChevronDown size={12} color={T.inkSecondary} />
            </>
          )}
        </div>
        <IconBtn icon={LogOut} title="Esci" />
      </div>
    </div>
  );
}

function IconBtn({ icon: Icon, title, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        position: "relative",
        width: 36,
        height: 36,
        borderRadius: 8,
        backgroundColor: "transparent",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: T.inkSecondary,
        cursor: "pointer",
        transition: "background-color 120ms",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.greenTint)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      <Icon size={18} />
      {badge && (
        <span
          style={{
            position: "absolute",
            top: 5,
            right: 5,
            minWidth: 16,
            height: 16,
            padding: "0 4px",
            borderRadius: 10,
            backgroundColor: T.greenPrimary,
            color: "#fff",
            fontSize: 9,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

/** Menu verticale principale */
function Sidebar({ collapsed, activeValue = "dashboard", onSelect }) {
  const width = collapsed ? 68 : 224;
  return (
    <nav
      style={{
        width,
        flexShrink: 0,
        backgroundColor: T.bgSurface,
        borderRight: `1px solid ${T.borderSoft}`,
        display: "flex",
        flexDirection: "column",
        transition: "width 200ms ease",
      }}
    >
      <div style={{ height: 44, borderBottom: `1px solid ${T.borderSoft}`, display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-end", padding: "0 10px" }}>
        <button
          onClick={() => onSelect?.("__toggle__")}
          style={{ width: 30, height: 30, borderRadius: 6, background: "transparent", border: "none", color: T.inkSecondary, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          title={collapsed ? "Espandi menu" : "Comprimi menu"}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
        {NAV.map((item) => {
          const Icon = item.icon;
          const isActive = activeValue === item.value;
          return (
            <button
              key={item.value}
              onClick={() => onSelect?.(item.value)}
              title={item.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: `calc(100% - 12px)`,
                margin: "1px 6px",
                padding: collapsed ? 0 : "0 12px",
                height: 40,
                borderRadius: 8,
                border: "none",
                backgroundColor: isActive ? T.greenTint : "transparent",
                color: isActive ? T.greenPrimary : T.inkBody,
                fontSize: 13.5,
                fontWeight: isActive ? 600 : 500,
                cursor: "pointer",
                position: "relative",
                justifyContent: collapsed ? "center" : "flex-start",
                fontFamily: T.font,
                textAlign: "left",
              }}
            >
              {isActive && (
                <span
                  style={{
                    position: "absolute",
                    left: -6,
                    top: 6,
                    bottom: 6,
                    width: 3,
                    borderRadius: "0 2px 2px 0",
                    backgroundColor: T.greenPrimary,
                  }}
                />
              )}
              <Icon size={18} strokeWidth={1.75} />
              {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
            </button>
          );
        })}
      </div>
      {!collapsed && (
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.borderSoft}`, fontSize: 10.5, color: T.inkMuted }}>
          Gestionale di Studio · v1.11
        </div>
      )}
    </nav>
  );
}

/** Sottomenu orizzontale contestuale */
function Subnav({ items = [], activeKey }) {
  return (
    <div
      style={{
        height: 42,
        backgroundColor: T.bgSurface,
        borderBottom: `1px solid ${T.borderSoft}`,
        display: "flex",
        alignItems: "stretch",
        gap: 4,
        padding: "0 20px",
        overflowX: "auto",
        flexShrink: 0,
      }}
    >
      {items.map((it) => {
        const active = activeKey === it.key;
        return (
          <button
            key={it.key}
            style={{
              padding: "0 14px",
              height: "100%",
              border: "none",
              background: "transparent",
              color: active ? T.greenPrimary : T.inkSecondary,
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              cursor: "pointer",
              position: "relative",
              fontFamily: T.font,
              whiteSpace: "nowrap",
            }}
          >
            {it.label}
            {active && (
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  right: 10,
                  bottom: 0,
                  height: 2,
                  backgroundColor: T.greenPrimary,
                  borderRadius: "2px 2px 0 0",
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Fascia informativa Studio + Agenzia (Brand Book v1.0 · 2 righe, max 85px) */
function StudioBar({ compact = false, expandable = false }) {
  const [expanded, setExpanded] = useState(true);
  if (compact) {
    return (
      <div
        style={{
          padding: "8px 20px",
          backgroundColor: T.bgSurface,
          borderBottom: `1px solid ${T.borderSoft}`,
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontSize: 12,
          color: T.inkSecondary,
          flexShrink: 0,
        }}
      >
        <Building2 size={14} color={T.greenPrimary} />
        <span style={{ fontWeight: 600, color: T.inkBody }}>Studio Bonaiuti · Gruppo Impresa STP a r.l.</span>
        <span>·</span>
        <span>Civitavecchia · 0766 581701 · P.IVA 15948751001</span>
        <button
          style={{ marginLeft: "auto", padding: "4px 10px", fontSize: 11, fontWeight: 600, color: T.greenPrimary, backgroundColor: T.greenTint, border: "none", borderRadius: 6, cursor: "pointer" }}
        >
          Dati Studio ▾
        </button>
      </div>
    );
  }
  if (expandable && !expanded) {
    return (
      <div
        style={{
          padding: "8px 20px",
          backgroundColor: T.bgSurface,
          borderBottom: `1px solid ${T.borderSoft}`,
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => setExpanded(true)}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: T.greenPrimary, backgroundColor: T.greenTint, border: "none", borderRadius: 6, cursor: "pointer" }}
        >
          <Building2 size={14} /> Dati Studio
        </button>
      </div>
    );
  }
  return (
    <div
      style={{
        padding: "8px 20px",
        backgroundColor: T.bgSurface,
        borderBottom: `1px solid ${T.borderSoft}`,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        flexShrink: 0,
        maxHeight: 85,
        overflow: "hidden",
      }}
    >
      {/* Riga 1: STUDIO + AGENZIA — griglia 2 blocchi, no wrap forzato */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 1px minmax(0, 1fr)", gap: 16, alignItems: "center" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 12px", minWidth: 0 }}>
          <StudioBadge label="STUDIO" color={T.greenPrimary} />
          <StudioField icon={Phone} label="Tel" value={STUDIO.studioTelefono} />
          <StudioField icon={Mail} label="Email" value={STUDIO.studioEmail} copyable />
          <StudioField icon={ShieldCheck} label="PEC" value={STUDIO.studioPec} copyable />
          <StudioField icon={FileText} label="SDI" value={STUDIO.studioSdi} copyable mono />
        </div>
        <div style={{ width: 1, height: 22, backgroundColor: T.borderSoft, justifySelf: "center" }} />
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 12px", minWidth: 0 }}>
          <StudioBadge label="AGENZIA" color={T.orange} />
          <StudioField icon={Phone} label="Tel" value={STUDIO.agenziaTelefono} />
          <StudioField icon={Mail} label="Email" value={STUDIO.agenziaEmail} copyable />
          <StudioField icon={ShieldCheck} label="PEC" value={STUDIO.agenziaPec} copyable />
          {expandable && (
            <button
              onClick={() => setExpanded(false)}
              title="Comprimi fascia"
              style={{ marginLeft: "auto", width: 22, height: 22, background: "transparent", border: "none", color: T.inkSecondary, cursor: "pointer" }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>
      {/* Divider sottile */}
      <div style={{ height: 1, backgroundColor: T.borderSoft, margin: "2px 0" }} />
      {/* Riga 2: dati fiscali + banca + IBAN — griglia flessibile */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 14px", minWidth: 0 }}>
        <StudioField label="REA" value={STUDIO.rea} />
        <StudioField label="C.F." value={STUDIO.codiceFiscale} mono />
        <StudioField label="P.IVA" value={STUDIO.partitaIva} mono />
        <StudioField icon={Landmark} label="Banca" value={STUDIO.banca} />
        <StudioField label="IBAN" value={STUDIO.iban} copyable mono />
      </div>
    </div>
  );
}

function StudioBadge({ label, color }) {
  return (
    <span
      style={{
        padding: "2px 8px",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        color: "#fff",
        backgroundColor: color,
        borderRadius: 4,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
}

function StudioField({ icon: Icon, label, value, copyable, mono }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, flexShrink: 0 }}>
      {Icon && <Icon size={12} color={T.inkMuted} />}
      <span style={{ color: T.inkMuted, fontSize: 11, fontWeight: 500 }}>{label}:</span>
      <span style={{ color: T.inkBody, fontSize: 12, fontFamily: mono ? "ui-monospace, SFMono-Regular, monospace" : T.font, fontWeight: 500 }}>
        {value}
      </span>
      {copyable && (
        <button
          title="Copia"
          style={{ marginLeft: 2, padding: 2, background: "transparent", border: "none", color: T.inkMuted, cursor: "pointer", display: "inline-flex" }}
        >
          <Copy size={11} />
        </button>
      )}
    </div>
  );
}

/**
 * Barra sezione — usata nelle pagine interne (NON Cruscotto).
 * Design System: 1 solo pulsante primario verde legato alla sezione,
 * eventuali altre azioni come pulsanti secondari (outline bianco).
 */
function SectionActionBar({ primary, secondary = [] }) {
  return (
    <div
      style={{
        padding: "10px 24px",
        backgroundColor: T.bgSurface,
        borderBottom: `1px solid ${T.borderSoft}`,
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
      }}
    >
      {primary && (
        <button
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "0 16px",
            height: 40,
            borderRadius: 8,
            backgroundColor: T.greenPrimary,
            color: "#fff",
            border: "none",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: T.font,
            boxShadow: "0 2px 6px rgba(11,107,79,0.18)",
          }}
        >
          <primary.icon size={16} />
          {primary.label}
        </button>
      )}
      {secondary.length > 0 && <div style={{ width: 1, height: 24, backgroundColor: T.borderSoft, margin: "0 4px" }} />}
      {secondary.map((s) => (
        <button
          key={s.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "0 12px",
            height: 36,
            borderRadius: 8,
            backgroundColor: T.bgSurface,
            color: T.inkBody,
            border: `1px solid ${T.borderSoft}`,
            fontSize: 12.5,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: T.font,
          }}
        >
          <s.icon size={14} color={T.inkSecondary} />
          {s.label}
        </button>
      ))}
    </div>
  );
}

/** Barra dei comandi rapidi (SOLO Cruscotto — Design System) */
function QuickBar() {
  const primary = { icon: Plus, label: "Nuova pratica" };
  const secondary = [
    { icon: UserPlus, label: "Nuovo cliente" },
    { icon: ReceiptText, label: "Nuova fattura" },
    { icon: FileSignature, label: "Nuovo preventivo" },
    { icon: StickyNote, label: "Nota diario" },
    { icon: ClipboardList, label: "Nota lavoro" },
    { icon: AlertTriangle, label: "Scadenze critiche" },
  ];
  return (
    <div
      style={{
        padding: "12px 24px",
        backgroundColor: T.bgSurface,
        borderBottom: `1px solid ${T.borderSoft}`,
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexShrink: 0,
      }}
    >
      <button
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "0 16px",
          height: 40,
          borderRadius: 8,
          backgroundColor: T.greenPrimary,
          color: "#fff",
          border: "none",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: T.font,
          boxShadow: "0 1px 2px rgba(11,107,79,0.15)",
        }}
      >
        <primary.icon size={16} />
        {primary.label}
      </button>
      <div style={{ width: 1, height: 24, backgroundColor: T.borderSoft, margin: "0 4px" }} />
      {secondary.map((c) => (
        <button
          key={c.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "0 12px",
            height: 36,
            borderRadius: 8,
            backgroundColor: T.bgSurface,
            color: T.inkBody,
            border: `1px solid ${T.borderSoft}`,
            fontSize: 12.5,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: T.font,
          }}
        >
          <c.icon size={14} color={T.inkSecondary} />
          {c.label}
        </button>
      ))}
    </div>
  );
}

/** Card contenitore standard */
function Card({ title, badge, count, headerRight, children, style }) {
  return (
    <div
      style={{
        backgroundColor: T.bgSurface,
        border: `1px solid ${T.borderSoft}`,
        borderRadius: 10,
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(16,35,63,0.03)",
        ...style,
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: `1px solid ${T.borderSoft}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {badge && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: badge,
                display: "inline-block",
              }}
            />
          )}
          <h3 style={{ fontSize: 13, fontWeight: 700, color: T.inkDark, margin: 0, letterSpacing: "0.01em", textTransform: "uppercase" }}>
            {title}
          </h3>
          {count != null && (
            <span style={{ fontSize: 12, color: T.inkSecondary, fontWeight: 500 }}>({count})</span>
          )}
        </div>
        {headerRight}
      </div>
      <div>{children}</div>
    </div>
  );
}

/** KPI Pillola */
function Kpi({ label, value, hint, tone = "default" }) {
  const colorMap = {
    default: T.inkDark,
    success: T.greenLight,
    warning: T.orange,
    danger: T.red,
    brand: T.greenPrimary,
  };
  return (
    <div
      style={{
        flex: 1,
        padding: "14px 16px",
        borderRight: `1px solid ${T.borderSoft}`,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ fontSize: 10.5, color: T.inkSecondary, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: colorMap[tone] || T.inkDark, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
      {hint && <div style={{ fontSize: 11, color: T.inkMuted }}>{hint}</div>}
    </div>
  );
}

/** Cruscotto / dashboard */
function DashboardPanels({ singleColumn = false }) {
  const scadenze = [
    { date: "08/07/2026", label: "Routing Strict Client", status: "Scaduta" },
    { date: "08/07/2026", label: "Routing Strict Fallback Client", status: "Scaduta" },
    { date: "15/07/2026", label: "Fornitura documenti PEC Aprola", status: "In corso" },
  ];
  const documenti = []; // vuoto → "Nessun documento in scadenza"
  const richiamare = [
    { name: "Folino Giovanna", type: "Presenza", date: "03/06/2026", note: "Scadenza completata: Test" },
    { name: "Compl. Test 1780…", type: "Presenza", date: "03/06/2026", note: "Test completion — Cliente" },
    { name: "Compl. Test 1780…", type: "Presenza", date: "03/06/2026", note: "Test completion — Cliente" },
  ];
  const fatture = [
    { num: "0001/P", client: "Folino Giovanna", amount: "167,54 €", days: 43 },
    { num: "0003/P", client: "Folino Giovanna", amount: "600,00 €", days: 43 },
    { num: "0004/P", client: "Gruppo Impresa Società tra Prof.", amount: "1 675,00 €", days: 43 },
    { num: "0005/P", client: "Gruppo Impresa Società tra Prof.", amount: "1 010,00 €", days: 43 },
    { num: "0006/P", client: "Gruppo Impresa Società tra Prof.", amount: "127,00 €", days: 42 },
  ];
  const cols = singleColumn ? "1fr" : "1fr 1fr";
  return (
    <div style={{ padding: singleColumn ? 16 : 20, display: "flex", flexDirection: "column", gap: singleColumn ? 12 : 16 }}>
      {/* Riga 1 */}
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: singleColumn ? 12 : 16 }}>
        <Card title="Scadenze del mese" badge={T.red} count={scadenze.length} headerRight={<LinkArrow label="Apri Scadenzario" />}>
          <ListRows>
            {scadenze.slice(0, 5).map((s, i) => (
              <ListRow key={i} left={<><Dot color={T.red} /><span style={{ color: T.inkSecondary, fontSize: 12, marginRight: 8, minWidth: 82, whiteSpace: "nowrap" }}>{s.date}</span><span style={{ fontSize: 13, color: T.inkBody, fontWeight: 500 }}>{s.label}</span></>} right={<Badge tone="danger" nowrap>{s.status}</Badge>} />
            ))}
          </ListRows>
        </Card>
        <Card title="Documenti clienti · Adempimenti" badge={T.greenLight} count={documenti.length} headerRight={<LinkArrow label="Gestisci documenti" />}>
          <EmptyState label="Nessun documento in scadenza" />
        </Card>
      </div>
      {/* Riga 2 */}
      <div style={{ display: "grid", gridTemplateColumns: cols, gap: singleColumn ? 12 : 16 }}>
        <Card title="Clienti da ricontattare" badge={T.blue} count={richiamare.length} headerRight={<LinkArrow label="Apri Diario" />}>
          <ListRows>
            {richiamare.map((r, i) => (
              <ListRow key={i} left={<><Users size={13} color={T.inkMuted} /><span style={{ fontSize: 13, color: T.inkBody, fontWeight: 500, marginRight: 6, whiteSpace: "nowrap" }}>{r.name}</span><Badge tone="info" nowrap>{r.type}</Badge><span style={{ fontSize: 12, color: T.inkSecondary, marginLeft: 8, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.note}</span></>} right={<span style={{ fontSize: 12, color: T.inkMuted, whiteSpace: "nowrap" }}>{r.date}</span>} />
            ))}
          </ListRows>
        </Card>
        <Card title="Fatture da incassare" badge={T.orange} count={fatture.length} headerRight={<LinkArrow label="Vai a Fatturazione" />}>
          <ListRows>
            {fatture.map((f, i) => (
              <ListRow key={i} left={<><Dot color={T.orange} /><span style={{ fontSize: 12, color: T.inkMuted, marginRight: 8, minWidth: 52, whiteSpace: "nowrap" }}>{f.num}</span><span style={{ fontSize: 13, color: T.inkBody, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.client}</span></>} right={<><span style={{ fontSize: 13, color: T.inkDark, fontWeight: 600, marginRight: 12, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{f.amount}</span><span style={{ fontSize: 12, color: T.orange, fontWeight: 600, whiteSpace: "nowrap" }}>{f.days}gg</span></>} />
            ))}
          </ListRows>
        </Card>
      </div>
      {/* Riepilogo */}
      <div
        style={{
          display: "flex",
          flexWrap: singleColumn ? "wrap" : "nowrap",
          backgroundColor: T.bgSurface,
          border: `1px solid ${T.borderSoft}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <Kpi label="Pratiche attive" value="38" hint="16A · 22S" tone="brand" />
        <Kpi label="Da fatturare" value="4" hint="444,00 €" />
        <Kpi label="Residuo incasso" value="9 863,54 €" hint="13 fatture" />
        <Kpi label="Fatture scadute" value="10" hint="6 > 30gg" tone="warning" />
        <Kpi label="Solleciti" value="10" hint="8 143,54 €" tone="danger" />
        <div style={{ flex: 1, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 4, minWidth: singleColumn ? 140 : undefined }}>
          <div style={{ fontSize: 10.5, color: T.inkSecondary, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Completate oggi</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.greenLight, letterSpacing: "-0.01em" }}>0</div>
          <div style={{ fontSize: 11, color: T.inkMuted }}>7 aperte</div>
        </div>
      </div>
    </div>
  );
}

function ListRows({ children }) {
  return <div style={{ display: "flex", flexDirection: "column" }}>{children}</div>;
}
function ListRow({ left, right }) {
  return (
    <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderBottom: `1px solid ${T.borderSoft}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>{left}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>{right}</div>
    </div>
  );
}
function Dot({ color }) {
  return <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0, display: "inline-block" }} />;
}
function Badge({ children, tone = "default", nowrap }) {
  const bg = { default: "#F3F4F6", success: "#E6F1EC", warning: "#FEF0DC", danger: "#FBE4E4", info: "#E7F0F9" }[tone];
  const color = { default: T.inkSecondary, success: T.greenPrimary, warning: T.orange, danger: T.red, info: T.blue }[tone];
  return (
    <span style={{ padding: "3px 8px", fontSize: 11, fontWeight: 600, backgroundColor: bg, color, borderRadius: 12, letterSpacing: "0.01em", whiteSpace: nowrap ? "nowrap" : "normal", display: "inline-block" }}>
      {children}
    </span>
  );
}
function LinkArrow({ label }) {
  return (
    <a
      href="#"
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: T.greenPrimary, textDecoration: "none" }}
      onClick={(e) => e.preventDefault()}
    >
      {label} <ArrowUpRight size={13} />
    </a>
  );
}
function EmptyState({ label }) {
  return (
    <div style={{ padding: "24px 16px", textAlign: "center", color: T.inkMuted, fontSize: 12 }}>
      <FileText size={22} color={T.borderStrong} style={{ margin: "0 auto 6px" }} />
      {label}
    </div>
  );
}

/** Footer compatto */
function Footer() {
  return (
    <footer
      style={{
        padding: "10px 24px",
        backgroundColor: T.bgSurface,
        borderTop: `1px solid ${T.borderSoft}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize: 11,
        color: T.inkSecondary,
        flexShrink: 0,
      }}
    >
      <div>Gestionale di Studio · Portale operativo · v1.10.1 · Ultimo aggiornamento 22/07/2026</div>
      <div>© 2026 Gruppo Impresa S.r.l. S.T.P. · C.F./P.IVA 15948751001 · Tutti i diritti riservati.</div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════════════
// LAYOUT: assembla shell completa per una sezione
// ═══════════════════════════════════════════════════════════════
function Shell({
  width = 1440,
  height = 900,
  collapsed = false,
  active = "dashboard",
  subnavKey = null,
  subnavActiveKey = null,
  showStudioBar = true,
  studioBarVariant = "full", // "full" | "compact"
  quickBar = false,          // true SOLO nel Cruscotto
  sectionAction = null,      // { primary, secondary } per pagine interne
  children,
}) {
  const subnavItems = subnavKey ? SUBNAV[subnavKey] : null;
  return (
    <div
      style={{
        width,
        height,
        display: "flex",
        flexDirection: "column",
        backgroundColor: T.bgPage,
        fontFamily: T.font,
        color: T.inkBody,
        borderRadius: 12,
        overflow: "hidden",
        border: `1px solid ${T.borderStrong}`,
        boxShadow: "0 8px 32px rgba(16,35,63,0.10)",
      }}
    >
      <TopBar />
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <Sidebar collapsed={collapsed} activeValue={active} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
          {subnavItems && <Subnav items={subnavItems} activeKey={subnavActiveKey || subnavItems[0].key} />}
          {showStudioBar && <StudioBar compact={studioBarVariant === "compact"} />}
          {quickBar && <QuickBar />}
          {sectionAction && <SectionActionBar {...sectionAction} />}
          <div style={{ flex: 1, overflow: "auto" }}>{children}</div>
          <Footer />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// LOGIN mockup
// ═══════════════════════════════════════════════════════════════
function LoginMock() {
  return (
    <div
      style={{
        width: 1440,
        height: 900,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: T.bgPage,
        position: "relative",
        overflow: "hidden",
        fontFamily: T.font,
        borderRadius: 12,
        border: `1px solid ${T.borderStrong}`,
        boxShadow: "0 8px 32px rgba(16,35,63,0.10)",
      }}
    >
      {/* wave decoration */}
      <svg viewBox="0 0 1440 200" style={{ position: "absolute", bottom: 0, left: 0, right: 0, width: "100%", height: 200 }} preserveAspectRatio="none">
        <path d="M0,100 C240,180 480,20 720,80 C960,140 1200,60 1440,100 L1440,200 L0,200 Z" fill="rgba(11,107,79,0.06)" />
        <path d="M0,140 C240,200 480,80 720,140 C960,200 1200,120 1440,160 L1440,200 L0,200 Z" fill="rgba(11,107,79,0.08)" />
      </svg>
      <div
        style={{
          position: "relative",
          width: 420,
          backgroundColor: T.bgSurface,
          borderRadius: 14,
          border: `1px solid ${T.borderSoft}`,
          boxShadow: "0 12px 40px rgba(16,35,63,0.12)",
          padding: 40,
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* Logo */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: T.red }} />
            <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: T.inkSecondary }} />
            <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#F2C94C" }} />
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.greenPrimary, letterSpacing: "0.06em" }}>STUDIO BONAIUTI</div>
          <div style={{ fontSize: 12, color: T.inkSecondary, letterSpacing: "0.02em" }}>Gruppo Impresa STP a r.l.</div>
          <div style={{ marginTop: 8, padding: "4px 12px", fontSize: 11, fontWeight: 700, color: "#fff", backgroundColor: T.greenPrimary, borderRadius: 4, letterSpacing: "0.1em" }}>
            GESTIONALE DI STUDIO
          </div>
        </div>

        <div style={{ height: 1, backgroundColor: T.borderSoft }} />

        <div style={{ textAlign: "center", fontSize: 14, color: T.inkBody, fontWeight: 500 }}>
          Accedi al tuo gestionale
        </div>

        <FormField label="Email o Username" value="paola.bonaiuti" />
        <FormField label="Password" type="password" value="••••••••••" showPassword />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, color: T.inkSecondary, cursor: "pointer" }}>
            <input type="checkbox" defaultChecked style={{ accentColor: T.greenPrimary }} /> Ricordami
          </label>
          <a href="#" onClick={(e) => e.preventDefault()} style={{ color: T.greenPrimary, textDecoration: "none", fontWeight: 500 }}>Password dimenticata?</a>
        </div>

        <button
          style={{
            height: 48,
            borderRadius: 10,
            backgroundColor: T.greenPrimary,
            color: "#fff",
            fontSize: 15,
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            letterSpacing: "0.02em",
            boxShadow: "0 2px 8px rgba(11,107,79,0.25)",
          }}
        >
          Accedi
        </button>

        <div style={{ textAlign: "center", fontSize: 11, color: T.inkMuted, lineHeight: 1.5 }}>
          Studio Bonaiuti · Gruppo Impresa STP a r.l.<br />
          v1.11.0-a · © 2026 · Tutti i diritti riservati
        </div>
      </div>
    </div>
  );
}
function FormField({ label, type = "text", value, showPassword }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: T.inkBody }}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type={type}
          defaultValue={value}
          style={{
            width: "100%",
            height: 44,
            padding: "0 14px",
            paddingRight: showPassword ? 44 : 14,
            borderRadius: 8,
            border: `1px solid ${T.borderStrong}`,
            fontSize: 14,
            color: T.inkBody,
            fontFamily: T.font,
            outline: "none",
            backgroundColor: T.bgSurface,
            boxSizing: "border-box",
          }}
        />
        {showPassword && (
          <button style={{ position: "absolute", right: 6, top: 6, width: 32, height: 32, background: "transparent", border: "none", color: T.inkSecondary, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Eye size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Modulo di inserimento (esempio)
// ═══════════════════════════════════════════════════════════════
function FormExample() {
  return (
    <div style={{ padding: 24 }}>
      <div style={{ backgroundColor: T.bgSurface, border: `1px solid ${T.borderSoft}`, borderRadius: 10, padding: 24, maxWidth: 900 }}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: T.inkDark, margin: 0 }}>Nuova Pratica</h2>
          <p style={{ fontSize: 13, color: T.inkSecondary, marginTop: 4, marginBottom: 0 }}>Inserisci i dati anagrafici e le informazioni essenziali della pratica.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <FormField label="Numero pratica *" value="GS-3215" />
          <FormField label="Data apertura *" type="date" value="2026-07-27" />
          <FormField label="Cliente *" value="Folino Giovanna" />
          <FormField label="Tipologia" value="Consulenza fiscale" />
          <FormField label="Ente coinvolto" value="Agenzia delle Entrate" />
          <FormField label="Scadenza" type="date" value="2026-09-30" />
          <div style={{ gridColumn: "1 / 3" }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: T.inkBody, display: "block", marginBottom: 6 }}>Descrizione</label>
            <textarea
              defaultValue="Assistenza per adempimenti fiscali annuali e predisposizione dichiarazione dei redditi."
              style={{ width: "100%", minHeight: 96, padding: 12, borderRadius: 8, border: `1px solid ${T.borderStrong}`, fontSize: 14, color: T.inkBody, fontFamily: T.font, outline: "none", resize: "vertical", boxSizing: "border-box" }}
            />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, borderTop: `1px solid ${T.borderSoft}`, paddingTop: 16 }}>
          <button style={{ padding: "0 20px", height: 44, borderRadius: 8, backgroundColor: T.bgSurface, color: T.inkBody, border: `1px solid ${T.borderStrong}`, fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
            Annulla
          </button>
          <button style={{ padding: "0 20px", height: 44, borderRadius: 8, backgroundColor: T.bgSurface, color: T.greenPrimary, border: `1px solid ${T.greenPrimary}`, fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
            Salva bozza
          </button>
          <button style={{ padding: "0 22px", height: 44, borderRadius: 8, backgroundColor: T.greenPrimary, color: "#fff", border: "none", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: T.font, boxShadow: "0 2px 6px rgba(11,107,79,0.20)" }}>
            Crea pratica
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Tabella (esempio Fatturazione)
// ═══════════════════════════════════════════════════════════════
function TableExample() {
  const rows = [
    { num: "0029/P", data: "28/06/2026", cliente: "APROLA POALR", imponibile: "394,07 €", iva: "90,16 €", totale: "500,00 €", incassato: "0,00 €", residuo: "500,00 €", stato: "Da Pagare", tone: "warning" },
    { num: "0028/P", data: "11/06/2026", cliente: "Gruppo Impresa Società tra Prof.", imponibile: "0,00 €", iva: "228,80 €", totale: "1 971,00 €", incassato: "0,00 €", residuo: "1 971,00 €", stato: "Da Pagare", tone: "warning" },
    { num: "0027/P", data: "11/06/2026", cliente: "Civitavecchia Servizi Pubblici", imponibile: "0,00 €", iva: "90,89 €", totale: "822,00 €", incassato: "0,00 €", residuo: "822,00 €", stato: "Da Pagare", tone: "warning" },
    { num: "0026/P", data: "11/06/2026", cliente: "Civitavecchia Servizi Pubblici", imponibile: "0,00 €", iva: "90,89 €", totale: "822,00 €", incassato: "—", residuo: "—", stato: "Nota Credito", tone: "info" },
    { num: "0025/P", data: "11/06/2026", cliente: "Civitavecchia Servizi Pubblici", imponibile: "0,00 €", iva: "90,89 €", totale: "822,00 €", incassato: "0,00 €", residuo: "0,00 €", stato: "Stornata", tone: "default" },
    { num: "0022/P", data: "07/06/2026", cliente: "Folino Giovanna", imponibile: "236,44 €", iva: "54,10 €", totale: "300,00 €", incassato: "300,00 €", residuo: "0,00 €", stato: "Pagata", tone: "success" },
  ];
  return (
    <div style={{ padding: 24 }}>
      {/* Header sezione */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: T.inkDark, margin: 0 }}>Fatture Emesse</h2>
          <p style={{ fontSize: 13, color: T.inkSecondary, marginTop: 4, marginBottom: 0 }}>23 di 24 (filtrate)</p>
        </div>
        {/* Riepiloghi in header — solo dati, nessun pulsante primario duplicato */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, flexShrink: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", padding: "0 12px", borderRight: `1px solid ${T.borderSoft}` }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: T.inkSecondary, textTransform: "uppercase", letterSpacing: "0.06em" }}>Totale</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.inkDark, whiteSpace: "nowrap" }}>27 567,00 €</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", padding: "0 12px", borderRight: `1px solid ${T.borderSoft}` }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: T.inkSecondary, textTransform: "uppercase", letterSpacing: "0.06em" }}>Incassato</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.greenLight, whiteSpace: "nowrap" }}>20 567,46 €</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", padding: "0 12px" }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: T.inkSecondary, textTransform: "uppercase", letterSpacing: "0.06em" }}>Residuo</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.orange, whiteSpace: "nowrap" }}>6 999,54 €</span>
          </div>
        </div>
      </div>

      {/* Toolbar filtri */}
      <div style={{ backgroundColor: T.bgSurface, border: `1px solid ${T.borderSoft}`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 320, minWidth: 200 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: 12, color: T.inkMuted }} />
          <input placeholder="Cerca per numero, cliente, note…" style={{ width: "100%", height: 36, padding: "0 12px 0 32px", borderRadius: 8, border: `1px solid ${T.borderSoft}`, fontSize: 13, fontFamily: T.font, outline: "none", boxSizing: "border-box" }} />
        </div>
        <select style={{ height: 36, padding: "0 12px", borderRadius: 8, border: `1px solid ${T.borderSoft}`, fontSize: 13, fontFamily: T.font, backgroundColor: T.bgSurface, color: T.inkBody }}>
          <option>2026</option>
        </select>
        <button style={{ height: 36, padding: "0 12px", borderRadius: 8, border: `1px solid ${T.borderSoft}`, backgroundColor: T.bgSurface, color: T.inkBody, fontSize: 13, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <Filter size={13} /> Filtri <Badge tone="info">1</Badge>
        </button>
        <button style={{ height: 36, padding: "0 12px", borderRadius: 8, border: "none", background: "transparent", color: T.inkSecondary, fontSize: 13, cursor: "pointer" }}>Reset</button>
        <div style={{ flex: 1 }} />
        <button style={{ height: 36, padding: "0 12px", borderRadius: 8, border: `1px solid ${T.borderSoft}`, backgroundColor: T.bgSurface, color: T.inkBody, fontSize: 13, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <Printer size={13} /> Stampa Lista (23)
        </button>
      </div>

      {/* Tabella — scroll orizzontale INTERNO se necessario, no scroll pagina */}
      <div style={{ backgroundColor: T.bgSurface, border: `1px solid ${T.borderSoft}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 1080, borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 82 }} />
              <col style={{ width: 100 }} />
              <col style={{ minWidth: 220 }} />
              <col style={{ width: 108 }} />
              <col style={{ width: 92 }} />
              <col style={{ width: 108 }} />
              <col style={{ width: 108 }} />
              <col style={{ width: 108 }} />
              <col style={{ width: 118 }} />
              <col style={{ width: 96 }} />
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: "#FAFBFC", borderBottom: `1px solid ${T.borderSoft}` }}>
                <Th>Numero</Th>
                <Th>Data</Th>
                <Th>Cliente</Th>
                <Th align="right">Imponibile</Th>
                <Th align="right">IVA</Th>
                <Th align="right">Totale</Th>
                <Th align="right">Incassato</Th>
                <Th align="right">Residuo</Th>
                <Th>Stato</Th>
                <Th align="center">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${T.borderSoft}` }}>
                  <Td nowrap><span style={{ fontWeight: 600, color: T.inkDark }}>{r.num}</span></Td>
                  <Td nowrap><span style={{ color: T.inkSecondary }}>{r.data}</span></Td>
                  <Td truncate title={r.cliente}><span style={{ color: T.inkBody, fontWeight: 500 }}>{r.cliente}</span></Td>
                  <Td align="right" mono nowrap>{r.imponibile}</Td>
                  <Td align="right" mono nowrap>{r.iva}</Td>
                  <Td align="right" mono bold nowrap>{r.totale}</Td>
                  <Td align="right" mono nowrap>{r.incassato}</Td>
                  <Td align="right" mono nowrap>{r.residuo}</Td>
                  <Td nowrap><Badge tone={r.tone} nowrap>{r.stato}</Badge></Td>
                  <Td align="center" nowrap>
                    <div style={{ display: "inline-flex", gap: 2 }}>
                      <IconBtnSm icon={Eye} title="Visualizza" />
                      <IconBtnSm icon={Edit} title="Modifica" />
                      <IconBtnSm icon={MoreHorizontal} title="Altre azioni" />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
function Th({ children, align = "left", width }) {
  return <th style={{ padding: "10px 12px", textAlign: align, fontSize: 11, fontWeight: 700, color: T.inkSecondary, textTransform: "uppercase", letterSpacing: "0.06em", width, borderBottom: "none", whiteSpace: "nowrap" }}>{children}</th>;
}
function Td({ children, align = "left", mono, bold, nowrap, truncate, title }) {
  return (
    <td
      title={title}
      style={{
        padding: "10px 12px",
        textAlign: align,
        fontSize: 13,
        color: bold ? T.inkDark : T.inkBody,
        fontWeight: bold ? 700 : 500,
        fontFamily: mono ? "ui-monospace, SFMono-Regular, monospace" : T.font,
        fontVariantNumeric: mono ? "tabular-nums" : undefined,
        whiteSpace: nowrap || truncate ? "nowrap" : "normal",
        overflow: truncate ? "hidden" : "visible",
        textOverflow: truncate ? "ellipsis" : "clip",
        maxWidth: truncate ? 0 : undefined,
      }}
    >
      {children}
    </td>
  );
}
function IconBtnSm({ icon: Icon, title }) {
  return (
    <button
      title={title}
      style={{ width: 28, height: 28, borderRadius: 6, background: "transparent", border: "none", color: T.inkSecondary, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = T.greenTint)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      <Icon size={14} />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// Finestra di conferma critica
// ═══════════════════════════════════════════════════════════════
function CriticalConfirmDialog() {
  return (
    <div style={{ width: 1200, height: 700, backgroundColor: "rgba(16,35,63,0.55)", position: "relative", borderRadius: 12, border: `1px solid ${T.borderStrong}`, boxShadow: "0 8px 32px rgba(16,35,63,0.10)", overflow: "hidden", fontFamily: T.font }}>
      {/* fake page background */}
      <div style={{ position: "absolute", inset: 0, backgroundColor: T.bgPage, opacity: 0.55, filter: "blur(2px)" }} />
      {/* dialog */}
      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 480, backgroundColor: T.bgSurface, borderRadius: 12, border: `1px solid ${T.borderStrong}`, boxShadow: "0 24px 64px rgba(16,35,63,0.28)", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px 12px", display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: "#FBE4E4", color: T.red, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <AlertTriangle size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: T.inkDark, margin: 0, marginBottom: 6 }}>Confermare eliminazione?</h3>
            <p style={{ fontSize: 13.5, color: T.inkBody, lineHeight: 1.5, margin: 0 }}>
              Stai per eliminare la fattura <b>0029/P</b> emessa a <b>APROLA POALR</b>.
              L&apos;operazione è <b>irreversibile</b>: la fattura non sarà più recuperabile e verrà rimossa dal registro.
            </p>
            <div style={{ marginTop: 10, padding: 10, backgroundColor: "#FBE4E4", border: `1px solid #F5C7C7`, borderRadius: 8, fontSize: 12, color: T.red, display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={13} /> L&apos;incasso registrato collegato verrà stornato automaticamente.
            </div>
          </div>
        </div>
        <div style={{ padding: "12px 22px 18px", display: "flex", justifyContent: "flex-end", gap: 10, borderTop: `1px solid ${T.borderSoft}`, marginTop: 8 }}>
          <button style={{ padding: "0 18px", height: 40, borderRadius: 8, backgroundColor: T.bgSurface, color: T.inkBody, border: `1px solid ${T.borderStrong}`, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>Annulla</button>
          <button style={{ padding: "0 20px", height: 40, borderRadius: 8, backgroundColor: T.red, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: T.font }}>
            <Trash2 size={14} /> Elimina fattura
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PAGE — assembla tutte le varianti + navigation
// ═══════════════════════════════════════════════════════════════
const VARIANTS = [
  { key: "login", label: "1. Login" },
  { key: "home-desktop-open", label: "2. Cruscotto desktop · menu aperto" },
  { key: "home-desktop-collapsed", label: "3. Cruscotto desktop · menu compatto" },
  { key: "home-notebook", label: "4. Cruscotto notebook (1280)" },
  { key: "home-tablet", label: "5. Cruscotto tablet (900)" },
  { key: "fatturazione", label: "6. Fatturazione + sottomenu" },
  { key: "clienti", label: "7. Clienti + sottomenu" },
  { key: "studiobar-full", label: "8. Fascia aziendale completa" },
  { key: "studiobar-compact", label: "9. Fascia aziendale compatta" },
  { key: "table", label: "10. Esempio tabella" },
  { key: "form", label: "11. Esempio modulo inserimento" },
  { key: "confirm", label: "12. Finestra conferma critica" },
];

export default function DesignPreviewPage() {
  const [selected, setSelected] = useState("login");

  const render = () => {
    switch (selected) {
      case "login":
        return <LoginMock />;
      case "home-desktop-open":
        return (
          <Shell width={1440} height={900} active="dashboard" quickBar>
            <DashboardPanels />
          </Shell>
        );
      case "home-desktop-collapsed":
        return (
          <Shell width={1440} height={900} collapsed active="dashboard" quickBar>
            <DashboardPanels />
          </Shell>
        );
      case "home-notebook":
        return (
          <Shell width={1280} height={800} active="dashboard" studioBarVariant="compact" quickBar>
            <DashboardPanels />
          </Shell>
        );
      case "home-tablet":
        return (
          <Shell
            width={900}
            height={800}
            collapsed
            active="dashboard"
            studioBarVariant="compact"
            sectionAction={{
              primary: { icon: Plus, label: "Nuova pratica" },
              secondary: [
                { icon: UserPlus, label: "Cliente" },
                { icon: ReceiptText, label: "Fattura" },
                { icon: MoreHorizontal, label: "Altro" },
              ],
            }}
          >
            <DashboardPanels singleColumn />
          </Shell>
        );
      case "fatturazione":
        return (
          <Shell
            width={1440}
            height={900}
            active="fatturazione"
            subnavKey="fatturazione"
            subnavActiveKey="elenco"
            studioBarVariant="compact"
            sectionAction={{
              primary: { icon: Plus, label: "Crea fattura" },
              secondary: [
                { icon: FileSignature, label: "Nuovo preventivo" },
                { icon: ReceiptText, label: "Registra incasso" },
                { icon: AlertTriangle, label: "Nuovo sollecito" },
                { icon: Printer, label: "Stampa" },
                { icon: MoreHorizontal, label: "Altro" },
              ],
            }}
          >
            <TableExample />
          </Shell>
        );
      case "clienti":
        return (
          <Shell
            width={1440}
            height={900}
            active="clients"
            subnavKey="clients"
            subnavActiveKey="elenco"
            studioBarVariant="compact"
            sectionAction={{
              primary: { icon: UserPlus, label: "Nuovo cliente" },
              secondary: [
                { icon: FileText, label: "Documenti" },
                { icon: BookUser, label: "Nota diario" },
                { icon: Mail, label: "Comunicazione" },
                { icon: Printer, label: "Stampa" },
                { icon: MoreHorizontal, label: "Altro" },
              ],
            }}
          >
            <ClientTablePreview />
          </Shell>
        );
      case "studiobar-full":
        return (
          <div style={{ width: 1200, backgroundColor: T.bgPage, padding: 24, borderRadius: 12, border: `1px solid ${T.borderStrong}`, boxShadow: "0 8px 32px rgba(16,35,63,0.10)" }}>
            <StudioBar />
            <p style={{ marginTop: 20, fontSize: 13, color: T.inkSecondary }}>Fascia aziendale in configurazione desktop: due blocchi distinti Studio + Agenzia (riga 1), REA / C.F. / P.IVA / Banca / IBAN (riga 2). Altezza max 85px, griglia responsive, nessuna sovrapposizione, nessuno scroll orizzontale.</p>
          </div>
        );
      case "studiobar-compact":
        return (
          <div style={{ width: 1200, backgroundColor: T.bgPage, padding: 24, borderRadius: 12, border: `1px solid ${T.borderStrong}`, boxShadow: "0 8px 32px rgba(16,35,63,0.10)" }}>
            <StudioBar compact />
            <p style={{ marginTop: 20, fontSize: 13, color: T.inkSecondary }}>Variante compatta per notebook/tablet — pulsante &quot;Dati Studio&quot; per espandere il pannello completo.</p>
          </div>
        );
      case "table":
        return (
          <Shell
            width={1440}
            height={900}
            active="fatturazione"
            subnavKey="fatturazione"
            subnavActiveKey="elenco"
            studioBarVariant="compact"
            sectionAction={{
              primary: { icon: Plus, label: "Crea fattura" },
              secondary: [
                { icon: FileSignature, label: "Nuovo preventivo" },
                { icon: AlertTriangle, label: "Nuovo sollecito" },
                { icon: Printer, label: "Stampa" },
              ],
            }}
          >
            <TableExample />
          </Shell>
        );
      case "form":
        return (
          <Shell
            width={1440}
            height={900}
            active="cases"
            subnavKey="cases"
            subnavActiveKey="nuova"
            studioBarVariant="compact"
            sectionAction={{ primary: { icon: Plus, label: "Nuova pratica" } }}
          >
            <FormExample />
          </Shell>
        );
      case "confirm":
        return <CriticalConfirmDialog />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#EEF2F5",
        padding: "24px 32px",
        fontFamily: T.font,
        color: T.inkBody,
      }}
      data-testid="design-preview-page"
    >
      {/* Header intro */}
      <div style={{ maxWidth: 1500, margin: "0 auto 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px", backgroundColor: T.greenPrimary, color: "#fff", borderRadius: 12, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>
              GRUPPO IMPRESA SUITE · GESTIONALE DI STUDIO
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: T.inkDark, margin: "8px 0 4px", letterSpacing: "-0.01em" }}>Direzione grafica v2 — Anteprima varianti</h1>
            <p style={{ fontSize: 14, color: T.inkSecondary, margin: 0, maxWidth: 720 }}>
              12 mockup statici basati sul <b>Brand Book v1.0</b>. Verde ufficiale <code style={{ backgroundColor: "#fff", padding: "1px 6px", borderRadius: 4, border: `1px solid ${T.borderSoft}`, fontSize: 12 }}>#0B6B4F</code>, font Inter, iconografia lucide-react.
              Nessuna modifica al database o ai flussi operativi. Serve solo a validare la direzione grafica prima del rollout.
            </p>
          </div>
          <a
            href="/"
            style={{ padding: "8px 16px", backgroundColor: T.bgSurface, border: `1px solid ${T.borderStrong}`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: T.inkBody, textDecoration: "none" }}
          >
            ← Torna al gestionale
          </a>
        </div>
      </div>

      {/* Selector */}
      <div style={{ maxWidth: 1500, margin: "0 auto 24px", display: "flex", flexWrap: "wrap", gap: 6 }}>
        {VARIANTS.map((v) => {
          const active = selected === v.key;
          return (
            <button
              key={v.key}
              onClick={() => setSelected(v.key)}
              data-testid={`variant-btn-${v.key}`}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                backgroundColor: active ? T.greenPrimary : T.bgSurface,
                color: active ? "#fff" : T.inkBody,
                border: `1px solid ${active ? T.greenPrimary : T.borderSoft}`,
                fontSize: 12.5,
                fontWeight: active ? 700 : 500,
                cursor: "pointer",
                fontFamily: T.font,
                transition: "all 120ms",
              }}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      {/* Mockup */}
      <div style={{ maxWidth: 1500, margin: "0 auto", display: "flex", justifyContent: "center", alignItems: "flex-start", overflow: "auto" }}>
        {render()}
      </div>
    </div>
  );
}

// Extra: table variant per Clienti
function ClientTablePreview() {
  const rows = [
    { name: "Folino Giovanna", type: "Persona fisica", cf: "FLNGNN75A41A345Z", city: "Civitavecchia", tel: "+39 349 123 45 67", email: "folino@example.it", stato: "Attivo", tone: "success" },
    { name: "Gruppo Impresa Srl STP", type: "Società tra Prof.", cf: "15948751001", city: "Civitavecchia", tel: "0766 581701", email: "info@gruppoimpresa.org", stato: "Attivo", tone: "success" },
    { name: "Civitavecchia Servizi Pubblici", type: "Ente pubblico", cf: "01234567890", city: "Civitavecchia", tel: "0766 500123", email: "info@csp.it", stato: "In verifica", tone: "warning" },
    { name: "APROLA POALR", type: "Persona fisica", cf: "PRLPLR60R15H501K", city: "Roma", tel: "—", email: "aprola@example.it", stato: "Attivo", tone: "success" },
    { name: "Compl. Test 1780", type: "Persona fisica", cf: "TSTCPL80A01H501Y", city: "Milano", tel: "—", email: "test1780@example.it", stato: "Sospeso", tone: "danger" },
  ];
  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: T.inkDark, margin: 0 }}>Elenco Clienti</h2>
          <p style={{ fontSize: 13, color: T.inkSecondary, marginTop: 4, marginBottom: 0 }}>487 clienti totali · 5 visibili</p>
        </div>
      </div>
      <div style={{ backgroundColor: T.bgSurface, border: `1px solid ${T.borderSoft}`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 380, minWidth: 220 }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: 12, color: T.inkMuted }} />
          <input placeholder="Cerca per nome, P.IVA, codice fiscale…" style={{ width: "100%", height: 36, padding: "0 12px 0 32px", borderRadius: 8, border: `1px solid ${T.borderSoft}`, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: T.font }} />
        </div>
        <button style={{ height: 36, padding: "0 12px", borderRadius: 8, border: `1px solid ${T.borderSoft}`, backgroundColor: T.bgSurface, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, color: T.inkBody, whiteSpace: "nowrap" }}>
          <Filter size={13} /> Filtri
        </button>
      </div>
      <div style={{ backgroundColor: T.bgSurface, border: `1px solid ${T.borderSoft}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 1040, borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ minWidth: 220 }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 160 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 150 }} />
              <col style={{ minWidth: 200 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 96 }} />
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: "#FAFBFC", borderBottom: `1px solid ${T.borderSoft}` }}>
                <Th>Nome</Th>
                <Th>Tipologia</Th>
                <Th>C.F. / P.IVA</Th>
                <Th>Città</Th>
                <Th>Telefono</Th>
                <Th>Email</Th>
                <Th>Stato</Th>
                <Th align="center">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${T.borderSoft}` }}>
                  <Td truncate title={r.name}><span style={{ fontWeight: 600, color: T.inkDark }}>{r.name}</span></Td>
                  <Td nowrap><span style={{ color: T.inkSecondary }}>{r.type}</span></Td>
                  <Td mono nowrap>{r.cf}</Td>
                  <Td nowrap>{r.city}</Td>
                  <Td nowrap>{r.tel}</Td>
                  <Td truncate title={r.email}>{r.email}</Td>
                  <Td nowrap><Badge tone={r.tone} nowrap>{r.stato}</Badge></Td>
                  <Td align="center" nowrap>
                    <div style={{ display: "inline-flex", gap: 2 }}>
                      <IconBtnSm icon={Eye} title="Visualizza" />
                      <IconBtnSm icon={Edit} title="Modifica" />
                      <IconBtnSm icon={MoreHorizontal} title="Altre azioni" />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

